import React from "react";
import { Button, Card, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Link } from "react-router-dom";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **PURCHASE-FUNNEL SURFACE — RENDERS PRICES AND THE CHECKOUT LINK.** The
 * `productId` that becomes `/buy?product=…` and every displayed price come
 * straight from props and are untouched; only styling moved.
 *
 * Rendered from `Home/TryBuyCta.tsx` (still v4, converts in T4). Neither call
 * site passes a `className`, so there is no cross-engine
 * `sx`-vs-JSS override to reconcile (guide §2, "`className` fallback props").
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `membershipOptionWrapper` and
 * `smlDivider` were never referenced from the JSX. `buttonPrimary` WAS
 * referenced but its body was empty `{}` — it emitted no CSS, so the
 * `isPrimary` branch below carries the base button rules only, exactly as v4
 * rendered them.
 */

// was: useStyles().button — the rules common to both branches.
// `color: "text.primary"` is NOT in the v4 rule: it restores the v4 Button
// default colour that v5+ removed (guide §2, "Button default color"). Without
// it the primary (text-variant) button would render lime-on-lime. The
// secondary branch below overrides it with its own explicit `#fff`, exactly as
// v4's `buttonSecondary` did.
const buttonBaseSx = {
  marginTop: "32px",
  marginLeft: 0,
  marginRight: 0,
  width: "100%",
  padding: "8px 14px",
  fontSize: "0.8rem",
  lineHeight: "1.2rem",
  color: "text.primary",
  "&:hover": {
    transform: "scale(1.02)",
    transition: "transform 0.2s ease-in-out",
  },
};

/**
 * was: `classnames(classes.button, { buttonSecondary: !isPrimary })`.
 *
 * v4 applied BOTH classes to the element, so the two `&:hover` blocks
 * co-existed as separate CSS rules on the same selector and their (disjoint)
 * declarations both applied — `transform`/`transition` from `button`,
 * `borderColor`/`color` from `buttonSecondary`. A flat object spread would
 * REPLACE the `&:hover` key and silently drop the scale-up, so the hover block
 * is merged explicitly.
 *
 * Everything else in `buttonSecondary` is a key `button` does not declare,
 * so the spread order below reproduces v4's JSS source order (later key wins).
 */
const buttonSecondarySx = (theme: Theme) => ({
  ...buttonBaseSx,
  border: "2px solid #fff",
  color: "#fff",
  "&:hover": {
    ...buttonBaseSx["&:hover"],
    borderColor: theme.palette.background.ffGreen,
    color: theme.palette.background.ffGreen,
  },
});

// was: useStyles().membershipOptionContainer
const membershipOptionContainerSx = (theme: Theme) => ({
  flex: 1,
  textAlign: "center",
  padding: "24px 12px",
  margin: 0,
  "&:first-child": {
    marginRight: "8px",
  },
  "&:last-child": {
    marginLeft: "8px",
  },
  backgroundColor: "background.ffNavyLighter",
  [theme.breakpoints.up("sm")]: {
    padding: "24px 32px",
  },
});

// was: useStyles().membershipOptionTitle
const membershipOptionTitleSx = {
  color: "background.ffGreen",
  fontSize: "1.8rem",
  marginBottom: "24px",
};

// was: useStyles().membershipOptionPrimaryPrice
const membershipOptionPrimaryPriceSx = (theme: Theme) => ({
  fontSize: "2.2rem",
  color: "common.white",
  fontWeight: 500,
  marginBottom: "24px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "3rem",
  },
  "& span": {
    fontSize: "1.4rem",
    fontWeight: "400",
  },
});

// was: useStyles().membershipOptionDescription
const membershipOptionDescriptionSx = {
  margin: "16px 0px",
};

export type SubscriptionPriceCardProps = {
  title: string;
  productId: string;
  duration: "monthly" | "yearly";
  gbpPrice: number;
  eurPrice: number;
  usdPrice: number;
  isPrimary: boolean;
};

export const SubscriptionPriceCard = ({
  title,
  productId,
  duration,
  gbpPrice,
  eurPrice,
  usdPrice,
  isPrimary,
}: SubscriptionPriceCardProps) => {
  return (
    <Card sx={membershipOptionContainerSx}>
      <Typography sx={membershipOptionTitleSx}>{title}</Typography>
      <Typography sx={membershipOptionPrimaryPriceSx}>
        &pound;{gbpPrice.toFixed(2)}
        <span>/{duration === "monthly" ? "Month" : "Year"}</span>
      </Typography>
      <Typography sx={membershipOptionDescriptionSx} variant="body1">
        <b>&euro;{eurPrice.toFixed(2)}</b> / <b>&#36;{usdPrice.toFixed(2)}</b>
      </Typography>

      <Link to={`/buy?product=${productId}`} style={{ textDecoration: "none" }}>
        <Button
          disableRipple
          size="large"
          variant={isPrimary ? undefined : "outlined"}
          sx={isPrimary ? buttonBaseSx : buttonSecondarySx}
        >
          Join Now
        </Button>
      </Link>
    </Card>
  );
};
